import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { useAuthenticationStore } from "../stores/auth";
import { v4 as uuidv4 } from "uuid";

const DOMAIN = "app.fruitify.co.uk";

const isDomain = () => {
  return window.location.hostname === DOMAIN;
};

const sendToMetaPixel = (eventName, props = {}, uniqueSessionEvent = false) => {
  if (!window.fbq || !isDomain()) return;
  const storage = sessionStorage;
  const sentMetaEventsKey = "sent_meta_events";
  const sentMetaEvents = JSON.parse(storage.getItem(sentMetaEventsKey) || "[]");

  // Skip if already sent in this session and uniqueSessionEvent is true
  if (uniqueSessionEvent && sentMetaEvents.includes(eventName)) return;
  if (!sentMetaEvents.includes(eventName)) {
    // Fire the correct Meta Pixel event
    switch (eventName) {
      case "register_landing":
        window.fbq("track", "Registering");
        break;
      case "registered":
        window.fbq("track", "CompleteRegistration", { ...props });
        break;
      case "subscribed":
        window.fbq("track", "Purchase", {
          value: props?.value || 0,
          currency: props?.currency || "USD",
          ...props,
        });
        break;
      default:
        window.fbq("trackCustom", eventName, { ...props });
    }

    // Mark the event as sent
    if (uniqueSessionEvent) {
      sentMetaEvents.push(eventName);
      storage.setItem(sentMetaEventsKey, JSON.stringify(sentMetaEvents));
    }
  }
};

const sendToXPixel = (eventName, uniqueSessionEvent = false) => {
  if (!window.twq || !isDomain()) return;
  const storage = sessionStorage;
  const sentXEventsKey = "sent_X_events";
  const sentXEvents = JSON.parse(storage.getItem(sentXEventsKey) || "[]");

  // Skip if already sent in this session and uniqueSessionEvent is true
  if (uniqueSessionEvent && sentXEvents.includes(eventName)) return;
  if (!sentXEvents.includes(eventName)) {
    // Fire the correct Meta Pixel event
    switch (eventName) {
      case "register_landing":
        window.twq("event", "tw-qqfrd-qrm7c");
        break;
      case "registered":
        window.twq("event", "tw-qqfrd-qrm71");
        break;
      case "subscribed":
        window.twq("event", "tw-qqfrd-qqfre");
        break;
    }

    if (uniqueSessionEvent) {
      sentXEvents.push(eventName);
      storage.setItem(sentXEventsKey, JSON.stringify(sentXEvents));
    }
  }
};

export function useTrackingAnalytics() {
  const location = useLocation();
  const hasCapturedPageRef = useRef(false);
  const { user } = useAuthenticationStore();

  // Store UTM params on initial visit
  // helper to read a cookie
  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  useEffect(() => {
    // 1. Try URL params first
    const params = new URLSearchParams(location.search);
    const utmSource = params.get("utm_source");
    const utmCampaign = params.get("utm_campaign");
    const utmMedium = params.get("utm_medium");

    let utmFound = false;

    if (utmSource || utmCampaign || utmMedium) {
      localStorage.setItem("utm_source", utmSource || "");
      localStorage.setItem("utm_campaign", utmCampaign || "");
      localStorage.setItem("utm_medium", utmMedium || "");
      utmFound = true;
    }

    // 2. If no URL params, check cookie set by main website
    if (!utmFound) {
      const cookieValue = getCookie("fruitify_utm");
      if (cookieValue) {
        try {
          const utmData = JSON.parse(cookieValue);
          if (utmData.utm_source)
            localStorage.setItem("utm_source", utmData.utm_source);
          if (utmData.utm_campaign)
            localStorage.setItem("utm_campaign", utmData.utm_campaign);
          if (utmData.utm_medium)
            localStorage.setItem("utm_medium", utmData.utm_medium);
        } catch (e) {
          console.error("Failed to parse UTM cookie", e);
        }
      }
    }
  }, [location]);

  const initPostHog = () => {
    if (!posthog.__initialized && isDomain()) {
      posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        capture_performance: false,
      });
    }
  };

  const identifyUser = (email, isIndependent, isStudent, extraProps = {}) => {
    if (!email && !isStudent) return;
    const currentId = posthog.get_distinct_id();
    let distinctId;
    let userProperties = {
      is_student: isStudent,
      independent_user: isIndependent,
      ...extraProps,
    };
    if (isStudent) {
      // For students, use an anonymous ID (or a pre-existing one from your DB)
      // We do NOT include the email property here to remain compliant
      distinctId = extraProps.student_id || uuidv4();
    } else {
      distinctId = email;
      userProperties.email = email;
    }
    if (currentId === distinctId) return;
    posthog.identify(distinctId, userProperties);
  };

  const capturePageView = (extraProps = {}) => {
    if (user.beingImpersonated) return;
    if (hasCapturedPageRef.current) return;

    posthog.capture("$pageview", {
      ...extraProps,
    });
    hasCapturedPageRef.current = true;
  };

  const recordMarketingEvent = useCallback(
    (eventName, extraProps = {}, uniqueSessionEvent = false) => {
      sendToMetaPixel(eventName, extraProps, uniqueSessionEvent);
      sendToXPixel(eventName, extraProps, uniqueSessionEvent);
      const storage = sessionStorage;
      const key = "sent_posthog_events";

      const sentEvents = JSON.parse(storage.getItem(key) || "[]");
      if (uniqueSessionEvent && sentEvents.includes(eventName)) {
        return;
      }
      if (!sentEvents.includes(eventName)) {
        posthog.capture(eventName, {
          utm_source: localStorage.getItem("utm_source"),
          utm_campaign: localStorage.getItem("utm_campaign"),
          utm_medium: localStorage.getItem("utm_medium"),
          ...extraProps,
        });

        if (uniqueSessionEvent) {
          sentEvents.push(eventName);
          storage.setItem(key, JSON.stringify(sentEvents));
        }
      }
    },
    [],
  );

  return { initPostHog, identifyUser, capturePageView, recordMarketingEvent };
}
